import { Label } from '@kcftech/design-system';
import { IValueUnitDto, ValueUnitDto } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { NumberInput } from 'components/forms/NumberInput';
import { TextInput } from 'components/forms/TextInput';
import { InlineEditor } from 'components/inline_edit/InlineEditor';
import { NoValue } from 'components/no_value/NoValue';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useInlineEditor } from 'custom_hooks/useInlineEditor';
import { isValidNumber } from 'utils/number';

const MaxValueUnitLength = 32;
const MaxValueUnitValue = 1_000_000_000_000;

export type LocationValueUnitCellProps = {
    locationId: string;
    data: IValueUnitDto | undefined;
    onChange: () => void;
};

export function LocationValueUnitCell({
    locationId,
    data,
    onChange,
}: LocationValueUnitCellProps) {
    const { canEditLocationValueUnit } = usePermissions();
    const { showToast } = useNotificationContext();
    const { mutate: updateValueUnit, isPending } = useMutation({
        meta: { name: 'set-location-value-unit' },
        mutationFn: async (values: Partial<IValueUnitDto> | undefined) => {
            if (!values?.UnitName || !values.UnitsPerHour) {
                throw new Error('value unit Name and UnitsPerHour are required');
            }
            await sdClient.setLocationValueUnit({
                id: locationId,
                body: new ValueUnitDto({
                    UnitName: values.UnitName,
                    UnitsPerHour: values.UnitsPerHour,
                }),
            });
        },

        onSuccess: () => {
            onChange();
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to save location value unit',
            }),
    });

    const {
        valueToRender,
        inputValue,
        error,
        setInputValue,
        isEditing,
        isConfirming,
        openEditor,
        closeEditor,
        confirm,
        canConfirm,
    } = useInlineEditor<Partial<IValueUnitDto>>({
        value: data,
        validate: (v) => {
            if (!v) {
                return undefined;
            }

            if (
                v.UnitName !== undefined &&
                v.UnitName !== '' &&
                v.UnitName.length > MaxValueUnitLength
            ) {
                return `Unit Name length must be ${MaxValueUnitLength} characters or less`;
            }

            if (
                v.UnitsPerHour !== undefined &&
                v.UnitName !== '' &&
                v.UnitsPerHour > MaxValueUnitValue
            ) {
                return `Units per Hour must be ${MaxValueUnitValue} or less`;
            }

            if (!v.UnitsPerHour) {
                return 'Units per Hour is required';
            }
            if (!v.UnitName) {
                return 'Units Name is required';
            }
        },
        onConfirm: (v) => updateValueUnit(v),
    });

    return (
        <InlineEditor
            value={
                valueToRender ? (
                    <>
                        {valueToRender.UnitsPerHour} {valueToRender.UnitName} per hour
                    </>
                ) : (
                    <NoValue />
                )
            }
            editor={
                <div className="flex gap-10">
                    <Label label="Units Per Hour">
                        <NumberInput
                            className="inline-block"
                            value={inputValue?.UnitsPerHour}
                            invalid={error !== undefined}
                            placeholder="Enter a number (e.g. 1, 2, 5)"
                            onChange={(e) => {
                                setInputValue((cur) => ({
                                    ...cur,
                                    UnitsPerHour: isValidNumber(e.target.value)
                                        ? Number(e.target.value)
                                        : undefined,
                                }));
                            }}
                            onKeyDown={(event) => event.stopPropagation()}
                        />
                    </Label>

                    <Label label="Units Name">
                        <TextInput
                            type="text"
                            className="inline-block"
                            value={inputValue?.UnitName}
                            placeholder="Enter a name (e.g. Bushels)"
                            invalid={error !== undefined}
                            onChange={(e) => {
                                setInputValue((cur) => ({
                                    ...cur,
                                    UnitName: e.target.value,
                                }));
                            }}
                            onKeyDown={(event) => event.stopPropagation()}
                        />
                    </Label>
                </div>
            }
            error={error}
            canClose={!isConfirming && !isPending}
            canEdit={canEditLocationValueUnit && !isConfirming}
            canConfirm={canConfirm && !isPending}
            isEditing={isEditing}
            isDisabled={!canEditLocationValueUnit || isConfirming}
            isPending={isConfirming || isPending}
            onClose={closeEditor}
            onEdit={openEditor}
            onConfirm={confirm}
        />
    );
}
